import { useCallback, useMemo } from "react";

import { useRouter } from "next/router";

import { GUEST_CHECKOUT_KEY } from "common/static/checkout/checkout.static";

import { useAxiomErrorReporter } from "@/common/hooks/useAxiomErrorReporter";
import useAddProductsToCart from "@whoppah/apollo/hooks/useAddProductsToCart";
import useGuestCart from "@whoppah/apollo/hooks/useGuestCart";
import CartCountGql from "@whoppah/apollo/query/cartCount.graphql";
import useCartHasItems from "@whoppah/components/shared/hooks/useCartHasItems";
import LanguageUtils from "@whoppah/utils/LanguageUtils";

const useGuestCheckout = () => {
  const router = useRouter();
  const { refetch: refetchCartHasItems } = useCartHasItems();
  const [addProductsToCart] = useAddProductsToCart();
  const { log } = useAxiomErrorReporter("checkout");

  const getProductIds = useCallback((): string[] => {
    if (typeof window === "undefined") {
      return [];
    }

    const ids = localStorage.getItem(GUEST_CHECKOUT_KEY);
    return ids ? JSON.parse(ids) : [];
  }, []);

  const {
    data,
    loading,
    refetch: refetchGuestCart,
  } = useGuestCart({
    variables: {
      productIds: getProductIds(),
      lang: LanguageUtils.getLanguage(router.locale),
    },
    skip: typeof window === "undefined",
    notifyOnNetworkStatusChange: true,
  });

  const addProduct = useCallback(
    async (id: string) => {
      const ids = localStorage.getItem(GUEST_CHECKOUT_KEY);
      const idsArray = ids ? JSON.parse(ids) : [];

      const uniqueIds = new Set(idsArray);
      uniqueIds.add(id);

      localStorage.setItem(
        GUEST_CHECKOUT_KEY,
        JSON.stringify(Array.from(uniqueIds))
      );

      // "Refresh" the cart has items reactive var.
      await refetchCartHasItems();
    },
    [refetchCartHasItems]
  );

  const removeProduct = useCallback(
    async (id: string) => {
      const ids = localStorage.getItem(GUEST_CHECKOUT_KEY);
      const idsArray = ids ? JSON.parse(ids) : [];

      const filteredIds = idsArray.filter(
        (productID: string) => productID !== id
      );

      localStorage.setItem(GUEST_CHECKOUT_KEY, JSON.stringify(filteredIds));

      await refetchGuestCart({
        productIds: getProductIds(),
      });

      // "Refresh" the cart has items reactive var.
      await refetchCartHasItems();
    },
    [refetchCartHasItems, refetchGuestCart, getProductIds]
  );

  const addProductToCart = useCallback(async () => {
    const ids = getProductIds();

    if (ids.length) {
      try {
        await addProductsToCart({
          variables: {
            productIds: ids,
          },
          refetchQueries: [{ query: CartCountGql }],
        });

        // clear local storage guest cart products after adding products to cart
        localStorage.removeItem(GUEST_CHECKOUT_KEY);
      } catch (error) {
        // Migrating the guest cart into the real cart after login can fail
        // silently here, leaving the user with an apparently empty cart
        // post-login. Log it for observability.
        log("useGuestCheckout", {
          message: (error as Error).message,
          stack: (error as Error).stack,
          type: "guest-cart-merge-failed",
          productIdsCount: ids.length,
        });
      }
    }
  }, [getProductIds, addProductsToCart, log]);

  const products = useMemo(() => {
    return data?.guestCart.products ?? [];
  }, [data?.guestCart.products]);

  return {
    addProduct,
    removeProduct,
    addProductToCart,
    getProductIds,
    products,
    loading,
  };
};

export default useGuestCheckout;
